
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import type { Department, StaffMember, Shift, Schedule, AdminUser, AssignmentStatus } from '../../types';
import { getWeekDates, formatDayAndDate, formatDateRange, formatDateYYYYMMDD } from '../../utils/dateUtils';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

export interface PrintData {
    departments: Department[];
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    weekStartDate: Date; 
    departmentId?: string; // New: filter by department
}

interface PrintPageProps extends PrintData {
    onDone: () => void;
}


const PrintPage: React.FC<PrintPageProps> = ({ departments, people, shifts, schedule, weekStartDate, departmentId, onDone }) => {
    const reportRef = useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        const originalBodyStyle = document.body.style.cssText;
        // Prepare body for printing by removing the default gray background
        document.body.style.cssText = 'background-color: white;';
        
        // A short timeout allows the component to fully render before the print dialog blocks the main thread.
        const timer = setTimeout(() => {
            window.print();
        }, 500); 

        return () => {
            clearTimeout(timer);
            // Restore original body style when the component unmounts
            document.body.style.cssText = originalBodyStyle;
        };
    }, []);

    const handleDownloadImage = async () => {
        if (!reportRef.current) return;
        
        setIsDownloading(true);
        const node = reportRef.current;
        
        // Store original styles to restore them after capture
        const scrollContainers = node.querySelectorAll('.overflow-x-auto, .overflow-y-auto, .overflow-auto');
        const originalStyles = new Map<HTMLElement, { overflow: string, height: string, width: string }>();
        
        scrollContainers.forEach(el => {
            const htmlEl = el as HTMLElement;
            originalStyles.set(htmlEl, {
                overflow: htmlEl.style.overflow,
                height: htmlEl.style.height,
                width: htmlEl.style.width
            });
        });
        
        const originalNodeStyle = node.style.cssText;

        try {
            // 1. Force all scroll containers to be visible and hide scrollbars
            scrollContainers.forEach(el => {
                const htmlEl = el as HTMLElement;
                htmlEl.style.overflow = 'visible';
                htmlEl.style.height = 'auto';
                htmlEl.style.width = 'auto';
                htmlEl.classList.add('no-scrollbar');
            });
            
            // 2. Expand main container to content size
            node.style.width = 'max-content';
            node.style.maxWidth = 'none';
            node.style.minWidth = 'unset';
            node.style.height = 'auto';
            node.style.maxHeight = 'none';
            node.style.minHeight = 'unset';

            // Give it a bit more time for styles to settle
            await new Promise(resolve => setTimeout(resolve, 150));

            const width = node.offsetWidth;
            const height = node.offsetHeight;

            const dataUrl = await toPng(node, {
                quality: 1.0,
                pixelRatio: 2, // Higher quality
                backgroundColor: 'white',
                cacheBust: true,
                width: width,
                height: height,
                style: {
                    margin: '0',
                    padding: '0',
                    transform: 'none',
                    width: `${width}px`,
                    height: `${height}px`,
                }
            });
            
            const link = document.createElement('a');
            link.download = `schedule-${formatDateYYYYMMDD(weekStartDate)}.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) {
            console.error('Failed to download image:', err);
        } finally {
            // 3. Restore original styles
            node.style.cssText = originalNodeStyle;
            scrollContainers.forEach(el => {
                const htmlEl = el as HTMLElement;
                htmlEl.classList.remove('no-scrollbar');
                const original = originalStyles.get(htmlEl);
                if (original) {
                    htmlEl.style.overflow = original.overflow;
                    htmlEl.style.height = original.height;
                    htmlEl.style.width = original.width;
                }
            });
            setIsDownloading(false);
        }
    };

    const weekDates = useMemo(() => getWeekDates(weekStartDate), [weekStartDate]);
    const sortedDepartments = useMemo(() => {
        return [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [departments]);
    const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));

    const filteredDepartments = useMemo(() => {
        if (!departmentId || departmentId === 'all') return sortedDepartments;
        return sortedDepartments.filter(d => d.id === departmentId);
    }, [sortedDepartments, departmentId]);

    const filteredPeople = useMemo(() => {
        if (!departmentId || departmentId === 'all') return people;
        return people.filter(p => p.departmentIds?.includes(departmentId));
    }, [people, departmentId]);

    const departmentStaffMap = useMemo(() => {
        const map = new Map<string, SchedulablePerson[]>();
        filteredDepartments.forEach(dep => map.set(dep.id, []));
        filteredPeople.forEach(person => {
            if (person.departmentIds && person.departmentIds.length > 0) {
                // Use primary department for mapping in print view
                const primaryDepId = person.departmentIds[0];
                if (map.has(primaryDepId)) {
                    map.get(primaryDepId)?.push(person);
                }
            }
        });
        return map;
    }, [filteredDepartments, filteredPeople]);

    const statusLabels: { [key in AssignmentStatus]?: string } = {
        OFF: 'OFF',
        SICK: 'SICK',
        VACATION: 'VACATION',
        ON_CALL: 'ON CALL',
        REFUSED: 'REFUSED',
        UNASSIGNED: '(off)',
    };

    return (
         <div className="p-4 sm:p-6 lg:p-8 bg-white min-h-screen">
            <style type="text/css" media="print">
            {`
                @page { size: landscape; margin: 10mm; }
                body { -webkit-print-color-adjust: exact; color-adjust: exact; }
                .no-print { display: none !important; }
            `}
            </style>

            {/* Control Bar */}
            <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
                <button
                    onClick={handleDownloadImage}
                    disabled={isDownloading}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                    {isDownloading ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    )}
                    {isDownloading ? 'Generating...' : 'Download Image'}
                </button>
                <button
                    onClick={() => window.print()}
                    className="bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print
                </button>
                <button 
                    onClick={onDone}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg shadow-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                </button>
            </div>

            <main ref={reportRef} className="bg-white p-4 w-[90%] mx-auto">
                <div className="text-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        Weekly Schedule:
                        <span className="ml-2 font-medium text-gray-600">{formatDateRange(weekStartDate)}</span>
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300">
                        <thead>
                            <tr className="bg-gray-100">
                                <th scope="col" className="py-0 px-2 border border-gray-300 text-left text-sm font-semibold text-gray-700 w-48">
                                    Staff Member
                                </th>
                                {weekDates.map((date) => {
                                    const { day, date: dateNum } = formatDayAndDate(date);
                                    return (
                                        <th key={date.toISOString()} scope="col" className="py-0 px-2 border border-gray-300 text-center text-sm font-semibold text-gray-700">
                                            {day} <span className="font-normal text-gray-600">{dateNum}</span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDepartments.map(dep => (
                                <React.Fragment key={dep.id}>
                                    <tr className="bg-gray-200">
                                        <td colSpan={8} className="py-0 px-2 border border-gray-300 text-sm font-bold text-gray-800">
                                            {dep.name}
                                        </td>
                                    </tr>
                                    {departmentStaffMap.get(dep.id)?.map(person => (
                                        <tr key={person.id} className="even:bg-gray-50 break-inside-avoid">
                                            <td className="py-0 px-2 border border-gray-300 font-medium text-gray-800">
                                                {person.name}
                                            </td>
                                            {weekDates.map(date => {
                                                const dateString = formatDateYYYYMMDD(date);
                                                const entry = schedule[person.id]?.[dateString];
                                                let content = <span className="text-gray-400">-</span>;
                                                let bgColor = 'transparent';

                                                if (entry) {
                                                    if (entry.status === 'ASSIGNED') {
                                                        const shift = entry.shiftId ? shiftsMap.get(entry.shiftId) : null;
                                                        const displayStartTime = entry.isTimeEdited ? entry.customStartTime : shift?.startTime;
                                                        const displayEndTime = entry.isTimeEdited ? entry.customEndTime : shift?.endTime;

                                                        if (displayStartTime && displayEndTime) {
                                                            const assignedDepartment = entry.departmentId 
                                                                ? departments.find(d => d.id === entry.departmentId)
                                                                : (person.departmentIds && person.departmentIds.length > 0 ? departments.find(d => d.id === person.departmentIds[0]) : null);
                                                                
                                                            content = (
                                                                <div className="font-semibold text-xs flex flex-col items-start relative h-full w-full justify-center">
                                                                    <span>{displayStartTime} - {displayEndTime}</span>
                                                                    {assignedDepartment && (
                                                                        <div className="absolute top-0 right-0 px-1 py-0.5 text-[7px] uppercase font-black bg-white text-gray-900 border-b border-l border-gray-200 leading-none">
                                                                            {assignedDepartment.abbreviation || assignedDepartment.name.substring(0, 3)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                            if (shift) {
                                                                bgColor = `${shift.color}20`; // Add alpha transparency
                                                            }
                                                        }
                                                    } else {
                                                        content = <div className="font-semibold text-xs">{statusLabels[entry.status]}</div>;
                                                        if (entry.status === 'OFF') bgColor = '#f3f4f6';
                                                        if (entry.status === 'VACATION') bgColor = '#dbeafe';
                                                        if (entry.status === 'SICK') bgColor = '#fef9c3';
                                                        if (entry.status === 'REFUSED') bgColor = '#f3e8ff'; // light purple
                                                    }
                                                }

                                                return (
                                                    <td
                                                        key={dateString}
                                                        style={{ backgroundColor: bgColor }}
                                                        className="py-0 px-2 border border-gray-300 text-left text-sm text-gray-800 align-middle"
                                                    >
                                                        {content}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
        </div>
    );
};

export default PrintPage;
